/**
 *
 * @description  甘特图编辑施工模板的子任务
 * @author xiongwei_zhang
 *
 * */

import ReactDOM from 'react-dom';
import moment from 'moment';
import { useEffect, useRef } from 'react';
import { message } from 'antd';
import { Gantt } from 'gantt_commercial';
import { postPlanSonEdit, editLink } from '&/api/personCenter/templateSettings';
// import LinksOfTask from '../LinksOfTask';
import DelLinkModal from '../DelLinkModal';
import useStoreState from '&/utils/customHooks/useStoreState';
import debounce from '&/utils/debounce';
import 'gantt_commercial/codebase/dhtmlxgantt.css';
import './EditGantt.scss';

function EditGantt(props) {
    let gantt = React.useMemo(Gantt.getGanttInstance, []);
    const { dataSource, onChange, forceResetLayout, highlightId } = props;

    const formatDate = (number, type) => {
        const targetDate = moment().startOf('year');
        return type
            ? targetDate.add(number, 'days')[`${type}Of`]('day').toDate()
            : targetDate.toDate();
    };

    const changeDateScale = tasks => {
        const endList = tasks.map(item => item.timeEnd);
        const endDateNum = endList.length ? Math.max(...endList) : 2;
        const minDay = formatDate();
        const maxDay = moment(minDay)
            .add(endDateNum - 1, 'days')
            .format('YYYY-MM-DD');
        if (!maxDay) return;
        let { scale_unit } = gantt.getState();
        const min = new Date(minDay);
        const max = new Date(maxDay);
        gantt.config.end_date = gantt.calculateEndDate(max, 2, scale_unit);
        gantt.config.start_date = gantt.calculateEndDate(min, 0, scale_unit);
    };

    // 必须延时！否则在事件的after回调时、可能操作还没彻底完成，undo不了本次的操作
    const undo = () => {
        const timmer = setTimeout(() => {
            gantt.undo();
            clearTimeout(timmer);
        }, 2);
    };

    const currentTemplateId = useRef();
    const effectTask = useRef();

    const afterGanttChange = id => {
        effectTask.current = id;
        onChange();
    };

    const cacheLink = useRef();

    const afterLinkAdd = (_, link) => {
        editLink({
            tempId: currentTemplateId.current,
            sourceId: link.source,
            targetId: link.target,
            type: link.type,
        })
            .then(() => {
                // 修改前的links里已有相同的任务及关联类型，则说明是依赖关系改变了
                if (
                    cacheLink.current.find(
                        el =>
                            el.source === link.source &&
                            el.target === link.target
                    )
                ) {
                    message.success('关联方式已修改');
                }
                afterGanttChange(link.source);
            })
            .catch(undo);
    };

    const linkInfoModal = useRef();

    const onDelLink = async id => {
        const link = gantt.getLink(id);
        gantt.deleteLink(id);
        return editLink({
            tempId: currentTemplateId.current,
            sourceId: link.source,
            targetId: link.target,
            dap: 1,
        })
            .then(({ msg }) => {
                message.success(msg);
                afterGanttChange(link.source);
            })
            .catch(undo);
    };

    const linkClickHandler = (id, e) => {
        const link = gantt.getLink(id);
        linkInfoModal.current.show({
            position: {
                left: e.pageX - 76 + 'px',
                top: e.pageY - 76 + 'px',
            },
            sourceTask: gantt.getTask(link.source),
            targetTask: gantt.getTask(link.target),
            type: link.type,
            id,
        });
        e.stopPropagation();
    };

    // link关系类型
    const { start_to_start, finish_to_start } = gantt.config.links;

    //  拦截创建link
    const beforeLinkAdd = (_, link) => {
        let allow = false;
        const targetTask = gantt.getTask(link.target);
        if (targetTask.type !== 'project') {
            const sourceTask = gantt.getTask(link.source);
            const { start_date } = targetTask;
            const { type } = link;
            // 开始之后且后置任务开始时间不超过前置任务的开始时间
            // 或
            // 结束之后且后置任务开始时间不超过前置任务结束时间
            allow =
                (type === start_to_start &&
                    sourceTask.start_date <= start_date) ||
                (type === finish_to_start && sourceTask.end_date <= start_date);
            allow = sourceTask.order < targetTask.order && allow;
        }

        return allow;
    };

    /* const tableRef = useRef();
    //点击角标
    const clickHandler = (taskId, evt) => {
        const domHelpers = gantt.utils.dom;
        const currentTask = gantt.getTask(taskId);

        if (domHelpers.closest(evt.target, '.gantt_left')) {
            // 把前置任务和当前任务链接起来的link
            const { pre } = currentTask;
            const row = gantt.getTask(pre.linkPlanId);
            row.linkType = pre.type;
            row.linkId = pre.linkId;
            tableRef.current.show([row], 0, {
                left: evt.pageX + 'px',
                top: evt.pageY + 'px',
            });
        } else if (domHelpers.closest(evt.target, '.gantt_right')) {
            const { rear } = currentTask;
            const rows = rear.map(e => {
                const t = gantt.getTask(e.linkPlanId);
                t.linkType = e.type;
                t.linkId = e.linkId;
                return t;
            });
            tableRef.current.show(rows, 1, {
                left: evt.pageX + 'px',
                top: evt.pageY + 'px',
            });
        }

        return true;
    }; */

    // 缓存展开的任务
    const expandIds = useRef(new Set());

    const recordOpened = (type = 'add', id) => {
        expandIds.current[type](id);
    };

    const getDateNum = date => {
        const startDate = moment().startOf('year');
        return Math.abs(startDate.diff(date, 'days'));
    };

    const dragId = useRef(); // 被拖动的任务id
    const drogInProgress = useRef(false); // 用户仍在拖动，停止清空拖动样式

    // 删除被拖动任务的高亮样式
    const removeDragStyle = (imm = false) => {
        if (!dragId.current) return;
        drogInProgress.current = false;
        if (imm) {
            dragId.current = null;
            gantt.refreshData();
            return;
        }
        const TIMMER = setTimeout(() => {
            if (!drogInProgress.current) {
                dragId.current = null;
                gantt.refreshData();
            }
            clearTimeout(TIMMER);
        }, 3_000);
    };

    const beforeTaskDrag = id => {
        dragId.current = +id;
        drogInProgress.current = true;
        return true;
    };

    // 子任务拖动结束回调
    const afterTaskDrag = async id => {
        const task = gantt.getTask(id);
        const { start_date, end_date } = task;

        const [momentStart, momentEnd] = [
            moment(start_date),
            moment(end_date).add(2, 's'),
        ]; // 加两秒是因为拖动开始日期时、结束日期会变成前一天的23：59：59
        try {
            if (momentStart.isSame(momentEnd, 'day')) {
                message.warning(
                    intl.get('templateSettings.cannotLessThanDay', { num: 1 })
                );
                throw new Error();
            }

            if (momentStart.isSame(moment().startOf('year'))) {
                message.warning(
                    intl.get('templateSettings.durationNotOptional', {
                        temp: 0,
                    })
                );
                throw new Error();
            }

            // 当前任务（拖拽后已被被修改的）
            const { pre } = task;
            if (pre) {
                let allow = true,
                    { type } = pre;
                // 有前置任务时..
                const prevTask = gantt.getTask(pre.linkPlanId);
                if (type === +finish_to_start) {
                    allow = momentStart.isSameOrAfter(
                        moment(prevTask.end_date).add(3, 's'),
                        'day'
                    );
                    !allow && message.warning('不能早于前置任务的结束时间');
                }

                if (type === +start_to_start) {
                    allow = momentStart.isSameOrAfter(
                        moment(prevTask.start_date),
                        'day'
                    );
                    !allow && message.warning('不能早于前置任务的开始时间');
                }

                if (!allow) {
                    removeDragStyle(true);
                    throw new Error();
                }
            }

            const timeAt = getDateNum(momentStart),
                timeEnd = getDateNum(momentEnd) - 1;

            const startChanged = +task.timeAt !== timeAt;
            const endChanged = +task.timeEnd !== timeEnd;

            let direction;
            if (startChanged && endChanged) {
                direction = 3;
            } else if (startChanged) {
                direction = 1;
            } else if (endChanged) {
                direction = 2;
            } else {
                // 没变化
                throw new Error();
            }

            const sendData = {
                sonId: task.id,
                timeAt,
                timeEnd,
                direction,
            };

            await postPlanSonEdit(sendData);
            message.success(intl.get('templateSettings.editSuccess'));
            afterGanttChange(id);
        } catch {
            undo();
        }
    };

    // 拖拽横道图时放大时间范围
    const changeScale = (id, mode, task) => {
        if (!['resize', 'move'].includes(mode)) return;

        let state = gantt.getState(),
            { scale_unit } = state;

        let scaleStep =
            gantt.date.add(new Date(), state.scale_step, state.scale_unit) -
            new Date();

        if (Math.abs(task.end_date - state.max_date) < scaleStep) {
            gantt.config.end_date = gantt.calculateEndDate(
                task.end_date,
                1,
                scale_unit
            );
        }

        gantt.render();
    };

    const destroyGantt = () => {
        gantt.destructor();
        gantt = undefined;
    };

    const ganttContainer = useRef();
    const highlightTasks = useRef([]);

    const highlightTask = (s, e, task) => {
        if (highlightTasks.current.includes(task.id)) {
            return 'active-row';
        }
    };

    const initGantt = () => {
        gantt.plugins({
            click_drag: true,
            drag_timeline: true,
            undo: true,
            critical_path: true,
        });

        Object.assign(gantt.config, {
            drag_project: false,
            columns: [
                {
                    name: 'name',
                    label: intl.get('projectSpace.project'),
                    resize: true,
                    width: '*',
                    tree: true,
                    onrender: item => {
                        return (
                            <div
                                title={item.name}
                                className={`text-no-wrap ${
                                    item.parent > 0 ? 'is--child' : ''
                                }`}>
                                {item.parent === 0 && (
                                    <i
                                        className={`filled icon-arrow icon-caret-down ${
                                            item.$open ? '' : 'is--rotate'
                                        }`}
                                        onClick={() => {
                                            gantt[
                                                item.$open ? 'close' : 'open'
                                            ](item.id);
                                        }}></i>
                                )}
                                {item.name}
                            </div>
                        );
                    },
                },
                {
                    name: 'timeLimit',
                    label: intl.get('projectSpace.times'),
                    align: 'center',
                    width: 64,
                    onrender({ timeLimit }) {
                        return (
                            <div className="times-wrap">
                                <span>{timeLimit}天</span>
                            </div>
                        );
                    },
                },
            ],
            show_progress: false,
            min_grid_column_width: 1,
            autoscroll: true,
            duration_unit: 'day',
            duration_step: 1,
            fit_tasks: true,
            drag_move: true,
            drag_resize: true,
            order_branch: false, // 暂时关闭排序 原来的值 'marker'
            order_branch_free: false,
            task_date: '%Y-%m-%d',
            reorder_grid_columns: false,
            min_column_width: 30,
            details_on_dblclick: false,
            drag_progress: false,
            correct_work_time: true, // 允许将任务的开始和结束日期调整为工作时间(在拖动时)
            scales: [
                {
                    unit: 'year',
                    step: 12,
                    format: (...arg) => {
                        return intl.get('templateSettings.ganttChart');
                    },
                }, // %M %F %m %n
                {
                    unit: 'day',
                    step: 1,
                    format: date => {
                        const startDate = moment().startOf('years');
                        const endDate = moment(date);
                        // return moment(date).dayOfYear()
                        return Math.abs(startDate.diff(endDate, 'days'));
                    }, // %d %l %j %D %y %m | format: function (date)
                },
            ],
            external_render: {
                isElement: React.isValidElement,
                renderElement: ReactDOM.render,
            },
            select_task: true,
            bar_height: 20,
            scale_height: 41,
            row_height: 41,
            autofit: false,
            layout: {
                css: 'gantt_container',
                cols: [
                    {
                        width: 218,
                        rows: [
                            {
                                view: 'grid',
                                scrollX: 'gridScroll',
                                scrollable: false,
                                scrollY: 'scrollVer',
                                scroll: 'y',
                            },
                        ],
                    },
                    { resizer: true, width: 1 },
                    {
                        rows: [
                            {
                                view: 'timeline',
                                scrollX: 'scrollHor',
                                scrollY: 'scrollVer',
                            },
                            {
                                view: 'scrollbar',
                                id: 'scrollHor',
                                group: 'horizontal',
                            },
                        ],
                    },
                    { view: 'scrollbar', id: 'scrollVer' },
                ],
            },
            keep_grid_width: true,
            container_resize_method: 'timeout',
            link_line_width: 1,
            link_arrow_size: 12,
            drag_links: true,
            // auto_scheduling: true,
            type_renderers: {
                // 自定义“项目”类型横道图
                project(task) {
                    const main_el = document.createElement('div');
                    main_el.setAttribute(gantt.config.task_attribute, task.id);
                    let size = gantt.getTaskPosition(task);
                    main_el.innerHTML = `<div class='gantt-task-progress-wrapper'><div class='ghost-item'></div></div><div class='project-content text-no-wrap'>${task.name}</div>`;
                    main_el.className = 'custom-project type--unstart';
                    main_el.style.left = size.left + 'px';
                    main_el.style.top = size.top + 10 + 'px';
                    main_el.style.width = size.width + 'px';
                    return main_el;
                },
            },
            smart_rendering: true,
            highlight_critical_path: true,
            round_dnd_dates: true,
        });

        Object.assign(gantt.templates, {
            task_class(s, e, task) {
                let res = 'type--unstart type--hover';
                if (dragId.current === +task.id) {
                    res += ' type--drag';
                }
                return res;
            },
            grid_row_class: highlightTask,
            task_row_class: highlightTask,
            task_text(s, e, task) {
                return task.name;
            },
            //  设置左右角标
            /* rightside_text(s, e, task) {
                return task.$source.length;
            },
            leftside_text(s, e, task) {
                return task.$target.length;
            }, */
            link_class(link) {
                return 'link--default';
            },
        });

        // gantt.attachEvent('onTaskClick', clickHandler);
        gantt.attachEvent('onLinkClick', linkClickHandler);
        gantt.attachEvent('onTaskOpened', recordOpened.bind(null, 'add'));
        gantt.attachEvent('onTaskClosed', recordOpened.bind(null, 'delete'));
        gantt.attachEvent('onBeforeLinkAdd', beforeLinkAdd);
        gantt.attachEvent('onAfterLinkAdd', afterLinkAdd);
        gantt.attachEvent('onAfterTaskDrag', afterTaskDrag);
        gantt.attachEvent('onTaskDrag', changeScale);
        gantt.attachEvent('onBeforeTaskDrag', beforeTaskDrag);

        gantt.init(ganttContainer.current);
    };

    useEffect(() => {
        initGantt();
        return destroyGantt;
    }, []);

    useEffect(() => {
        forceResetLayout > 0 && gantt.resetLayout();
    }, [forceResetLayout]);

    const resetLayout = debounce(() => {
        gantt.resetLayout();
    }, 350);

    useEffect(() => {
        window.addEventListener('resize', resetLayout);
        return () => {
            window.removeEventListener('resize', resetLayout);
        };
    }, []);

    // 当前宏任务更新完后...
    const afterUpdate = () =>
        new Promise(resole => {
            const TIMEOUT_ID = setTimeout(() => {
                resole();
                clearTimeout(TIMEOUT_ID);
            }, 0);
        });

    const first = useRef(true);

    // 自动高亮并展开施工阶段
    const highlightAndExpansionStage = async () => {
        try {
            if (!highlightId) {
                highlightTasks.current = [];
                gantt.refreshData();
                return;
            }

            // 首次延时执行。防止子任务未渲染
            if (first.current) {
                await afterUpdate();
                first.current = false;
            }

            gantt.open(highlightId);
            const tasks = gantt.getChildren(highlightId);
            tasks.push(highlightId);
            highlightTasks.current = tasks;
            gantt.showTask(highlightId);
            gantt.refreshData();
        } catch (error) {
            console.log(error, 'error');
        }
    };

    const templateManage = useStoreState('templateManage');

    // 数据更新
    const updateGantt = () => {
        currentTemplateId.current = dataSource.id;
        const { son: stages = [] } = dataSource;
        if (!stages.length) {
            gantt.clearAll();
            gantt.parse({ tasks: [], links: [] });
            gantt.render();
            return;
        }

        const tasks = [];
        const targetDate = moment().startOf('year');
        let order = 0;
        for (const item of stages) {
            if (item.son)
                for (const sonItem of item.son) {
                    sonItem.order = order++;
                    const plan = Object.assign(sonItem, {
                        parent: sonItem.pid,
                        type: 'task',
                        start_date: targetDate
                            .clone()
                            .add(sonItem.timeAt, 'days')
                            .startOf('day')
                            .toDate(),
                        end_date: targetDate
                            .clone()
                            .add(sonItem.timeEnd, 'days')
                            .endOf('day')
                            .toDate(),
                    });

                    tasks.push(plan);
                }

            const project = Object.assign(item, {
                parent: 0,
                type: 'project',
                start_date: targetDate
                    .clone()
                    .add(item.timeAt, 'days')
                    .startOf('day')
                    .toDate(),
                end_date: targetDate
                    .clone()
                    .add(item.timeEnd, 'days')
                    .endOf('day')
                    .toDate(),
            });

            tasks.push(project);
        }
        gantt.clearAll();
        changeDateScale(tasks);
        cacheLink.current = dataSource.links;
        gantt.parse({ tasks, links: dataSource.links });

        // 刷新数据后重新展开 "更新前展开任务"，（更新后会自动收起）
        if (expandIds.current.size) {
            const ids = expandIds.current.values();
            for (const id of ids) gantt.open(id);
        }

        // 展开更新前操作的task的整个施工阶段
        if (effectTask.current) {
            const pid = gantt.getParent(effectTask.current);
            gantt.open(pid);
            const tasks = gantt.getChildren(pid);
            tasks.push(pid);
            highlightTasks.current = tasks;
        }

        // 展开新增施工阶段所在的模板下所有的施工阶段
        if (templateManage.hasIds) {
            console.log(templateManage.expandStageIds);
            for (const stageId of templateManage.expandStageIds)
                gantt.open(stageId);
            templateManage.setExpandStageIds([]);
        }
        highlightAndExpansionStage();
        gantt.render();

        // 显示更新前操作的task
        if (effectTask.current) {
            gantt.showTask(effectTask.current);
            effectTask.current = null;
        }
        removeDragStyle();
    };

    useEffect(updateGantt, [dataSource]);

    useEffect(highlightAndExpansionStage, [highlightId]);

    return (
        <>
            <div ref={ganttContainer} className="edit-gantt-el" />
            <DelLinkModal ref={linkInfoModal} onDelLink={onDelLink} />
            {/* <LinksOfTask ref={tableRef} onDelLink={onDelLink} /> */}
        </>
    );
}

export default EditGantt;
